import { Component, Input, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { BriefingService } from '../services';
import { takeUntil, filter, flatMap, map, catchError, tap } from 'rxjs/operators';
import { Subject, of } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
    selector: 'briefing-slack-settings-editor',
    template: `
        <slack-link-button *ngIf="!form?.get('settings').get('slackToken').value" style="display: block; padding: 40px;"></slack-link-button>
        <div *ngIf="form?.get('settings').get('slackToken').value" [formGroup]="form" style="padding: 20px;" fxLayout="column" fxLayoutAlign="space-evenly">
            <div fxLayout="column">
                <mat-form-field>
                    <input matInput type="text" placeholder="Title" formControlName="briefingTitle">
                    <mat-error *ngIf="form.get('briefingTitle').hasError('required')">Title cannot be blank</mat-error>
                </mat-form-field>
                <mat-form-field>
                    <input matInput type="text" placeholder="Description" formControlName="description">
                    <mat-error *ngIf="form.get('description').hasError('required')">Description cannot be blank</mat-error>
                </mat-form-field>
                <ng-container formGroupName="commentary">
                    <mat-form-field>
                        <input matInput type="text" placeholder="Commentary Title" formControlName="title">
                    </mat-form-field>
                    <mat-form-field>
                        <input matInput type="text" placeholder="Commentary" formControlName="body">
                    </mat-form-field>
                </ng-container>
                <mat-form-field formGroupName="settings">
                    <mat-select placeholder="Slack Channel" formControlName="slackChannel">
                        <mat-option *ngFor="let channel of slackChannels" [value]="channel.id">{{ channel.name }}</mat-option>
                    </mat-select>
                    <mat-error *ngIf="form.get('settings').get('slackChannel').hasError('required')">Slack Channel cannot be blank</mat-error>
                </mat-form-field>
            </div>
        </div>
    `
})
export class BriefingSlackSettingsEditorComponent implements OnInit, OnDestroy {
    @Input() form : FormGroup;
    slackChannels: any;
    private cleanupTime = new Subject<void>();

    constructor(private snackBar: MatSnackBar, private briefingService: BriefingService) {}

    ngOnInit(): void { 
        this.form.get('settings').get('slackToken').valueChanges
        .pipe(
            filter(val => !!val),
            takeUntil(this.cleanupTime)
        ).subscribe(slackToken => this.fetchSlackDetails(slackToken));

        if (this.form && !this.form.get('settings').get('slackToken').value) {
            this.briefingService.getSlackTokenForNewBriefing()
            .pipe(filter(response => !!response?.token))
            .subscribe(response => {
                this.form.get('settings').get('slackToken').setValue(response.token);
            });
            
        } else if(this.form && this.form.get('settings').get('slackToken').value) {
            this.fetchSlackDetails(this.form.get('settings').get('slackToken').value);
        }
    }
 
    fetchSlackDetails(slackToken: string): void {
        this.briefingService.getSlackChannels(slackToken)
        .pipe(
            catchError(() => {
                this.form.get('settings').get('slackToken').setValue('');
                this.snackBar.open('Unable to fetch slack channels, please connect with a slack account with sufficient permissions.', 'OK', { duration: 20000 });
                return of(undefined);
            }),
            filter(channels => !!channels),
            map(channels => channels.filter(channel => !channel.is_archived && channel.is_member && !channel.is_read_only)),
        ).subscribe(channels => {
            this.slackChannels = channels;
            const currentChannel = this.form.get('settings').get('slackChannel').value;
            if (currentChannel?.length && !channels.find(channel => channel.id === currentChannel)) {
                this.form.get('settings').get('slackChannel').setValue('');
                this.snackBar.open('You no longer have access to the selected slack channel, please select another.', 'OK', { duration: 20000 });
            }
        });
    }   

    ngOnDestroy(): void {
        this.cleanupTime.next();
        this.cleanupTime.complete();
    }
}